/**
 * Batch 18-F36 — Technician-facing Training Evidence Summary
 *                read hook.
 *
 * Pulls own-rows-only evidence for a single (technician, artifact)
 * pair and returns a typed `TrainingEvidenceDisplayRow[]` array
 * that the F32 component already knows how to render.
 *
 * Policy / boundary contract (locked by F31, F33, F34, F35):
 *
 *   - Browser Supabase client only. No service-role usage. RLS is
 *     the permission boundary. Concretely, the existing policies
 *     on `training_consumption_sessions` (technician_users-joined
 *     own-rows path) and `training_completion_verifications`
 *     ("Technicians can view own verification rows") gate
 *     ownership; the `.eq('technician_profile_id', ...)` and
 *     `.eq('artifact_id', ...)` clauses below are belt-and-
 *     suspenders, not the permission check.
 *
 *   - Explicit column projections only. No `select('*')`. The
 *     selected columns omit every raw / private field enumerated
 *     in F35 §7 (session tokens, client_event_id, observed_url,
 *     observed_host, matched_opened_url, extension_version,
 *     user_agent, extension_evidence_summary, opened_url_snapshot,
 *     metadata, provider payloads, customer / vehicle / ticket
 *     payload columns, domain-mismatch diagnostic columns).
 *
 *   - No Edge Function calls. No domain-mismatch RPC. No mutation.
 *     Read-only.
 *
 *   - `requiresManagerVerification` is returned as `undefined` in
 *     v1. F35 §6 established that no source for this flag exists
 *     anywhere in the repo today. The F32 derivation helper
 *     treats `undefined === false` for step 3; "manual
 *     completion alone" therefore correctly resolves to
 *     `'manual_submitted'`. A future explicit batch may add the
 *     flag's home (artifact-level, requirement-level, or shop
 *     policy); the hook will then return a non-undefined value
 *     without breaking the component contract. Do not invent the
 *     flag.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  buildManagerRevocationRow,
  buildManagerVerificationRow,
  buildManualCompletionRow,
  type TrainingEvidenceDisplayRow,
} from '@/utils/training/evidenceDisplayRows';

export interface UseTechnicianTrainingEvidenceSummaryRowsArgs {
  technicianProfileId: string;
  artifactId: string;
  enabled?: boolean;
}

export interface UseTechnicianTrainingEvidenceSummaryRowsResult {
  rows: TrainingEvidenceDisplayRow[];
  isLoading: boolean;
  error: Error | null;
  requiresManagerVerification?: boolean;
}

// Explicit projected shapes. These names + types are the ONLY columns
// the hook selects from each table. Adding a new field here is the
// only safe way to surface more data and must clear the F35 §7
// exclusion list.

interface SessionRowProjection {
  id: string;
  manual_marked_complete_at: string | null;
  completion_status: string | null;
}

interface VerificationRowProjection {
  id: string;
  verification_status: 'verified' | 'revoked';
  verification_note: string | null;
  verified_by_role: string | null;
  created_at: string;
}

// Batch 18-F41 — the F40 plan locked this as the only requirement
// column the hook may select. Every other column on
// `training_requirements` (org_id, team_id, role_key, domain_key,
// requirement_rule, required_by_date, created_by, metadata) is
// outside the F35 §7 exclusion list at this boundary.
interface RequirementVerificationProjection {
  id: string;
  requires_manager_verification: boolean;
}

/**
 * Pure deterministic resolver for the verification-required flag.
 *
 * F40 §6 / §11 chose "any matching requirement with TRUE → TRUE"
 * as the conservative default. If a single active requirement on
 * the artifact considers verification mandatory, the technician
 * summary surfaces `'verification_pending'`.
 *
 * Returns:
 *   - `undefined` when zero rows reach the resolver (no active
 *     requirement for the artifact reaches the caller; the F32
 *     helper treats this as "no gate"),
 *   - `true` when any row's `requires_manager_verification === true`,
 *   - `false` when all rows are explicitly `false`.
 *
 * Not exported — behaviour tests exercise the resolver through the
 * hook surface to keep the public API minimal.
 */
function resolveRequiresManagerVerification(
  rows: RequirementVerificationProjection[],
): boolean | undefined {
  if (rows.length === 0) return undefined;
  if (rows.some((r) => r.requires_manager_verification === true)) return true;
  return false;
}

/**
 * Compare ISO timestamps newest-first. Null / missing timestamps
 * sink to the bottom — they never beat a real timestamp.
 */
function compareRowsNewestFirst(
  a: TrainingEvidenceDisplayRow,
  b: TrainingEvidenceDisplayRow,
): number {
  if (!a.occurredAt && !b.occurredAt) return 0;
  if (!a.occurredAt) return 1;
  if (!b.occurredAt) return -1;
  return b.occurredAt.localeCompare(a.occurredAt);
}

export function useTechnicianTrainingEvidenceSummaryRows(
  args: UseTechnicianTrainingEvidenceSummaryRowsArgs,
): UseTechnicianTrainingEvidenceSummaryRowsResult {
  const technicianProfileId = args.technicianProfileId;
  const artifactId = args.artifactId;
  const callerEnabled = args.enabled ?? true;
  const enabled = Boolean(technicianProfileId && artifactId && callerEnabled);

  const query = useQuery({
    queryKey: [
      'technician-training-evidence-summary-rows',
      technicianProfileId,
      artifactId,
    ],
    queryFn: async (): Promise<{
      rows: TrainingEvidenceDisplayRow[];
      requiresManagerVerification: boolean | undefined;
    }> => {
      // Defensive: also gated by `enabled` above. If both ids are
      // empty strings we still return the empty shape here.
      if (!technicianProfileId || !artifactId) {
        return { rows: [], requiresManagerVerification: undefined };
      }

      // Three scoped reads, run in parallel. Sessions and
      // verifications are RLS-gated to the current technician's own
      // rows + the specific artifact. The requirements read is
      // shop-scoped by the existing RLS
      // ("Shop members can view training requirements"
      //  USING can_access_shop(auth.uid(), org_id)) — the hook does
      // NOT add `.eq('org_id', ...)` because that would be a
      // client-side security filter, which F40 §6 explicitly
      // forbids. RLS is the shop boundary.
      const [sessionsRes, verificationsRes, requirementsRes] =
        await Promise.all([
          supabase
            .from('training_consumption_sessions')
            // Explicit projection — never select('*'). See header
            // policy contract.
            .select('id,manual_marked_complete_at,completion_status')
            .eq('technician_profile_id', technicianProfileId)
            .eq('artifact_id', artifactId),
          supabase
            .from('training_completion_verifications')
            .select(
              'id,verification_status,verification_note,verified_by_role,created_at',
            )
            .eq('technician_profile_id', technicianProfileId)
            .eq('artifact_id', artifactId),
          // Batch 18-F41 — verification-required flag source.
          // Explicit projection of ONLY the two columns we need;
          // every other requirement column (org_id, team_id,
          // role_key, domain_key, requirement_rule,
          // required_by_date, created_by, metadata) is excluded at
          // this boundary.
          supabase
            .from('training_requirements')
            .select('id,requires_manager_verification')
            .eq('artifact_id', artifactId)
            .eq('is_active', true),
        ]);

      if (sessionsRes.error) throw sessionsRes.error;
      if (verificationsRes.error) throw verificationsRes.error;
      // Treat a requirements read failure the same as the other
      // query failures: surface it as the React Query error so the
      // modal renders the neutral "Training status is unavailable
      // right now." copy. F40 §6 explicitly forbids silently
      // downgrading `true` → `false` on requirement-read errors.
      if (requirementsRes.error) throw requirementsRes.error;

      const sessionRows = (sessionsRes.data ?? []) as SessionRowProjection[];
      const verificationRows = (verificationsRes.data ??
        []) as VerificationRowProjection[];
      // Batch 18-F43 — generated Supabase types now include
      // `requires_manager_verification`, so the F41 temporary
      // `unknown` cast is no longer needed. The narrow projection
      // cast keeps the explicit shape contract.
      const requirementRows = (requirementsRes.data ??
        []) as RequirementVerificationProjection[];

      const out: TrainingEvidenceDisplayRow[] = [];

      // Manual completion rows — only sessions with a non-null
      // `manual_marked_complete_at`. The `manager_verified_at`
      // fallback is intentionally NOT used in v1: the canonical
      // source for verification rows is the audit table, and using
      // both would produce duplicate verified rows (one from the
      // session pointer, one from the audit row written by the
      // Edge Function). F35 §2 / §4 documents this choice.
      for (const s of sessionRows) {
        if (!s.manual_marked_complete_at) continue;
        out.push(
          buildManualCompletionRow({
            id: `mc:${s.id}`,
            manualMarkedCompleteAt: s.manual_marked_complete_at,
            completionStatus: s.completion_status ?? null,
            artifactTitle: null,
          }),
        );
      }

      // Manager verification / revocation rows from the audit table.
      // `verification_status` is constrained by the table CHECK to
      // 'verified' | 'revoked'.
      for (const v of verificationRows) {
        if (v.verification_status === 'verified') {
          out.push(
            buildManagerVerificationRow({
              id: `mv:${v.id}`,
              managerVerifiedAt: v.created_at,
              managerVerificationNote: v.verification_note ?? null,
              artifactTitle: null,
            }),
          );
        } else if (v.verification_status === 'revoked') {
          // Neutral revocation row only — revocation flavor decoding
          // (auto-revoked / one-shot reconciliation) is intentionally
          // NOT performed here. The audit table's `metadata` JSONB
          // column is on the F35 §7 exclusion list and the F36
          // prompt forbids selecting it. The row builder's default
          // label ("Verification revoked") is correct for v1; a
          // future explicit batch can add a non-JSONB flavor column
          // and wire it in without changing this hook's interface.
          out.push(
            buildManagerRevocationRow({
              id: `mr:${v.id}`,
              revokedAt: v.created_at,
              revocationNote: v.verification_note ?? null,
              autoRevoked: null,
              oneShotReconciliation: null,
              artifactTitle: null,
            }),
          );
        }
      }

      // Newest-first. Null timestamps to the bottom.
      out.sort(compareRowsNewestFirst);
      return {
        rows: out,
        requiresManagerVerification: resolveRequiresManagerVerification(
          requirementRows,
        ),
      };
    },
    enabled,
  });

  const rows = useMemo<TrainingEvidenceDisplayRow[]>(
    () => query.data?.rows ?? [],
    [query.data],
  );
  const requiresManagerVerification = query.data?.requiresManagerVerification;

  return {
    rows,
    isLoading: enabled ? query.isLoading : false,
    error: (query.error as Error | null) ?? null,
    // Batch 18-F41 — flag now resolved from `training_requirements`
    // by the deterministic resolver above (any matching active
    // requirement with TRUE wins; all FALSE → FALSE; zero matching
    // rows → UNDEFINED). The F32 derivation helper still treats
    // UNDEFINED identically to FALSE for step 3, so a technician
    // with no matching requirement keeps the v1 behavior
    // (`'manual_submitted'`).
    requiresManagerVerification,
  };
}
